import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Card, Form, Layout, Space, Tabs } from 'antd';
import { Routes } from '../../../routes';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { useCreateBadgeMutation, useDeleteBadgeMutation, useBadgeQuery, usePatchBadgeMutation } from '../../gql/hooks/useBadgeQueries';
import BasicsTab from '../GenericCreateEdit/BasicsTab';
import Sidebar from '../GenericCreateEdit/SidebarActions';
import GenericTab, { TAB_STATES } from '../GenericCreateEdit/GenericTab';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import useCreateImageThumbnail from '../../gql/hooks/useCreateImageThumbnail';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { useCreateEditGeneric } from '../GenericCreateEdit/useCreateEditGeneric';
import { Switch } from 'antd';
import { Flex } from 'antd';
import ImageCropUpload from '../GenericCreateEdit/ImageCropUpload';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';


function formatLocaleForm( t ) {
    return { locale: t.code, name: t.name, description: t.description };
}

function formatBadgeForm( b ) {
    return {
        id: b.badgeId,
        translations: b.locales.map( formatLocaleForm ),
        isPublished: b.isPublished,
        isRepeatable: b.isRepeatable,
        isFeatured: false,
        imageChanged: false,
        image: b.image,
    };
}

function formatBadgeInput( b ) {
    return {
        badgeId: b.id,
        name: b.translations?.find( t => t.locale === DEFAULT_LOCALE_CODE ).name,
        description: b.translations?.find( t => t.locale === DEFAULT_LOCALE_CODE ).description,
        image: b.image,
        isRepeatable: b.isRepeatable,
        isPublished: b.isPublished,
        isFeatured: b.isFeatured,
        locales: b.translations?.map( t => ( { code: t.locale, name: t.name, description: t.description } ) ),
        weight: 0,
    };
}

const creationInitialValues = {
    translations: [ { locale: 'en-US' } ],
    isPublished: false,
    isRepeatable: false,
    isFeatured: false,
    weight: 0,
    imageChanged: false,
};

const creationTabStates = {
    basics: [ TAB_STATES.REQUIRED ],
    badge: [ TAB_STATES.REQUIRED ],
};

const CreateEditBadgePage = ( { route: { params: { badgeId } } } ) => {

    const basicsRef = useRef(), imageRef = useRef();

    const [ fetchBadge ] = useBadgeQuery();
    const [ createBadgeMutation ] = useCreateBadgeMutation();
    const [ patchBadgeMutation ] = usePatchBadgeMutation();
    const [ deleteBadgeMutation ] = useDeleteBadgeMutation();

    async function beforeSave( { form, input } ) {
        const data = { ...input };
        if ( form.getFieldValue( 'imageChanged' ) === true ) {

            const image = await imageRef.current?.onSubmit();

            if ( image === false ) {
                throw new Error( 'Could not save badge icon' );
            }

            data.image = image;
        }
        return data;
    }

    const {
        isCreating, loading, error, setActiveTab, activeTab, isPublished,
        form, initialValues,
        item: badge,
        getTabIcon, tabStates,
        onValidateTab,
        messageContext,
        sidebarProps, tabProps,
    } = useCreateEditGeneric( {
        id: badgeId, idName: 'badgeId',
        label: 'badge',
        beforeSave,
        editRoute: Routes.EditBadge, viewRoute: Routes.ViewBadgesPage,
        creationTabStates, creationInitialValues,
        createMutation: createBadgeMutation, 
        fetch: fetchBadge,
        patchMutation: patchBadgeMutation, 
        deleteMutation: deleteBadgeMutation,
        formatDataToForm: formatBadgeForm, formatFormToData: formatBadgeInput
    } );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Badges', path: Routes.ViewBadgesPage.buildPath() },
        { name: isCreating ? 'Create' : 'Edit' }
    ];

    const canDelete = useMemo( () => !( badge?.personBadgeCount > 0 ), [ badge ] );
    const tip = useMemo( () => badge?.personBadgeCount > 0 ? <ExpandableAlert message={ `${ badge.personBadgeCount } users have already recieved this badge` } description={ 'Badge can not be deleted' } /> : undefined, [ badge ] );

    const tabItems = useMemo( () => [
        {
            key: 'basics',
            label: 'Basics',
            icon: getTabIcon( tabStates.basics ),
            children: <Space className='body'>
                <BasicsTab { ...tabProps } ref={ basicsRef } statuses={ tabStates.basics } onValidateCb={ statuses => onValidateTab( 'basics', statuses ) } disabled={ !!error } />
                <Sidebar { ...sidebarProps } tab='basics' tabFields={ basicsRef?.current?.formFields } canDelete={ canDelete } disabled={ !!error } 
                />
            </Space>
        },
        {
            key: 'badge',
            label: 'Badge',
            icon: getTabIcon( tabStates.badge ),
            children: <Space className='body'>
                <BadgeTab { ...tabProps } ref={ imageRef } statuses={ tabStates.badge } onValidateCb={ statuses => onValidateTab( 'badge', statuses ) } disabled={ !!error } />
                <Sidebar { ...sidebarProps } tab='badge' tabFields={ imageRef?.current?.formFields } canDelete={ canDelete } disabled={ !!error } 
                />
            </Space>
        },
    ], [ tabProps, isCreating, tabStates, sidebarProps, canDelete ] );


    return (
        <Layout className="badge-edit-page">

            { messageContext }

            <Breadcrumbs crumbs={ breadcrumbs } />

            <Card
                className="badge-edit-content"
                title={ isCreating ? 'Create Badge' : 'Edit Badge' }
            >
                { error && error }
                <Form form={ form } initialValues={ initialValues } layout='vertical' >

                    { tip && tip }

                    <Tabs
                        type="card"
                        size="large"
                        activeKey={ activeTab }
                        onChange={ setActiveTab }
                        items={ tabItems }
                        tabBarExtraContent={ {
                            right: loading ? <></> : <ColoredTag.Publish isPublished={ isPublished } />
                        } }
                    />
                </Form>

            </Card>

        </Layout>
    );
};

const BadgeTab = ( { ref, form, statuses, parentError, loading, onValidateCb, disabled } ) => {
    const formNames = [ 'image', 'isRepeatable' ];

    const { createImageThumbnail } = useCreateImageThumbnail();

    const [ uploadFile, setUploadFile ] = useState( null );

    useImperativeHandle( ref, () => ( {
        onSubmit,
        formFields: formNames
    } ) );

    async function onSubmit() {
        if ( form.getFieldValue( 'imageChanged' ) === true ) {
            const image = uploadFile ? await createImageThumbnail( uploadFile ) : false;
            if ( image !== false ) {
                form.setFieldValue( 'image', image );
            }
            return image;
        }
        return form.getFieldValue( 'image' );
    }

    return <GenericTab loading={ loading } parentError={ parentError } statuses={ statuses } title='Badge Information' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { validateTab, makeDirty } ) => (
            <Flex vertical={ false }>
                <Card
                    style={ { maxWidth: 400 } }
                    title='Badge Icon'
                    className='image-card'
                    extra={ <Tooltip title='PNG files only. Recommended 200x200px with transparent background.' ><QuestionCircleOutlined /></Tooltip> }
                    >
                    <Form.Item
                        name='image'
                        rules={ [
                            {
                                required: true,
                                message: 'Badge icon is required'
                            }
                        ] }>
                        <ImageCropUpload
                            disabled={ disabled }
                            name='Badge Icon'
                            title='Crop Badge Icon'
                            pngOnly
                            onFileChange={ file => {
                                setUploadFile( file );
                                form.setFieldsValue( { imageChanged: true } );
                                makeDirty?.();
                                validateTab?.();
                            } }
                        />
                    </Form.Item>
                </Card>
                <Card
                    style={ { maxWidth: 400 } }
                    title='Repeatability'
                    extra={ <Tooltip title='Whether the badge can be awarded multiple times to a user' ><QuestionCircleOutlined /></Tooltip> }
                    >
                    <Form.Item
                        name='isRepeatable'
                        >
                        <RepeatabilityFormItem onChange={ () => makeDirty() } />
                    </Form.Item>
                </Card>
            </Flex>
        ) }
    </GenericTab>;
};

const RepeatabilityFormItem = ( { value, onChange } ) => {

    return <Space direction='vertical'>
        <Switch className='repeatable-switch' checked={ value } onChange={ onChange } />
        <ColoredTag.Repeatable isRepeatable={ value } />
        <span className='canceled'>{ value ? 'A user can achieve this badge multiple times' : 'A user can only achieve this badge once' }</span>
    </Space>;
};

export default CreateEditBadgePage;
